// src/pages/purchases/PurchaseForm.js
import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Box, Typography, IconButton, Divider,
  CircularProgress, Alert, InputAdornment,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';

import { ActionButton, SecondaryButton } from '../../components/common/ActionButton';
import { ALICUOTAS_IVA } from '../../config/impuestos';

const TIPOS_DOCUMENTO = [
  { valor: 'factura', etiqueta: 'Factura' },
  { valor: 'nota_credito', etiqueta: 'Nota de crédito' },
  { valor: 'nota_debito', etiqueta: 'Nota de débito' },
  { valor: 'importacion', etiqueta: 'Importación' }
];

/** Los renglones de monto, en el orden en que aparecen en una factura. */
const ALICUOTAS = [
  { clave: 'general', base: 'baseGeneral', iva: 'ivaGeneral' },
  { clave: 'reducida', base: 'baseReducida', iva: 'ivaReducida' },
  { clave: 'adicional', base: 'baseAdicional', iva: 'ivaAdicional' }
];

const CAMPOS_MONTO = [
  'baseGeneral', 'ivaGeneral', 'baseReducida', 'ivaReducida',
  'baseAdicional', 'ivaAdicional', 'exento', 'noGravado', 'ivaRetenido', 'totalConIva'
];

const vacio = {
  fecha: '',
  tipoDocumento: 'factura',
  rifProveedor: '',
  nombreProveedor: '',
  numeroFactura: '',
  numeroControl: '',
  documentoAfectado: '',
  baseGeneral: '', ivaGeneral: '',
  baseReducida: '', ivaReducida: '',
  baseAdicional: '', ivaAdicional: '',
  exento: '', noGravado: '',
  ivaRetenido: '', comprobanteRetencion: '',
  totalConIva: '',
  notas: ''
};

const aNumero = (v) => Number(String(v).replace(',', '.')) || 0;
const bs = (n) => n.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const estiloCampo = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
    '&.Mui-focused fieldset': { borderColor: '#F5A623' }
  }
};

/**
 * Carga de una compra para el Libro de Compras.
 *
 * El formulario transcribe una factura de papel, así que está armado para
 * copiar en el mismo orden en que los datos aparecen impresos: proveedor,
 * identificación del documento y después los montos.
 *
 * Los montos no se calculan solos, se copian. Lo único que el formulario hace
 * por su cuenta es sugerir el IVA de una base recién escrita —la cuenta que
 * cualquiera haría a mano— y sumar el total. Las dos cosas se pueden pisar: si
 * la factura del proveedor dice 161,00 donde la cuenta da 160,00, el libro
 * tiene que decir 161,00.
 */
export const PurchaseForm = ({ open, onClose, purchase, onSave }) => {
  const [formData, setFormData] = useState(vacio);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [errorAlGuardar, setErrorAlGuardar] = useState(null);
  const [totalTocado, setTotalTocado] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrores({});
    setErrorAlGuardar(null);
    setTotalTocado(false);

    if (purchase) {
      const cargada = { ...vacio };
      Object.keys(vacio).forEach((k) => {
        const v = purchase[k];
        cargada[k] = v === undefined || v === null ? '' : String(v);
      });
      cargada.fecha = purchase.fecha ? String(purchase.fecha).slice(0, 10) : '';
      setFormData(cargada);
      setTotalTocado(true); // lo guardado manda; no se recalcula solo
    } else {
      // Una compra nueva se fecha hoy: es lo más probable.
      setFormData({ ...vacio, fecha: new Date().toISOString().slice(0, 10) });
    }
  }, [purchase, open]);

  const sumaDeComponentes = ALICUOTAS.reduce(
    (suma, a) => suma + aNumero(formData[a.base]) + aNumero(formData[a.iva]),
    aNumero(formData.exento) + aNumero(formData.noGravado)
  );
  const totalEscrito = aNumero(formData.totalConIva);
  const diferencia = totalTocado ? totalEscrito - sumaDeComponentes : 0;
  const esNota = formData.tipoDocumento === 'nota_credito' || formData.tipoDocumento === 'nota_debito';

  const cambiar = (campo) => (e) => {
    const valor = e.target.value;
    setFormData((prev) => {
      const siguiente = { ...prev, [campo]: valor };
      // Mientras nadie toque el total, lo lleva el formulario.
      if (!totalTocado && campo !== 'totalConIva') {
        const suma = ALICUOTAS.reduce(
          (s, a) => s + aNumero(siguiente[a.base]) + aNumero(siguiente[a.iva]),
          aNumero(siguiente.exento) + aNumero(siguiente.noGravado)
        );
        siguiente.totalConIva = suma ? suma.toFixed(2) : '';
      }
      return siguiente;
    });
    if (campo === 'totalConIva') setTotalTocado(true);
    setErrores((prev) => ({ ...prev, [campo]: undefined }));
  };

  /** Al salir de una base, si el IVA está vacío se sugiere el de esa alícuota. */
  const sugerirIva = (alicuota) => () => {
    const base = aNumero(formData[alicuota.base]);
    if (!base || aNumero(formData[alicuota.iva])) return;
    const sugerido = (base * ALICUOTAS_IVA[alicuota.clave].valor).toFixed(2);
    cambiar(alicuota.iva)({ target: { value: sugerido } });
  };

  const validar = () => {
    const e = {};
    if (!formData.fecha) e.fecha = 'La fecha es requerida';
    if (!formData.rifProveedor.trim()) e.rifProveedor = 'El RIF es requerido';
    if (!formData.nombreProveedor.trim()) e.nombreProveedor = 'El nombre es requerido';
    if (!formData.numeroFactura.trim()) e.numeroFactura = 'El número es requerido';
    if (totalEscrito <= 0) e.totalConIva = 'El total tiene que ser mayor que cero';
    setErrores(e);
    return Object.keys(e).length === 0;
  };

  const guardar = async () => {
    if (!validar()) return;
    setGuardando(true);
    setErrorAlGuardar(null);
    try {
      const datos = { ...formData };
      CAMPOS_MONTO.forEach((c) => { datos[c] = aNumero(datos[c]); });
      if (!esNota) datos.documentoAfectado = '';
      if (purchase?._id) datos._id = purchase._id;
      await onSave(datos);
      onClose();
    } catch (error) {
      setErrorAlGuardar(error.message || 'No se pudo guardar la compra.');
    } finally {
      setGuardando(false);
    }
  };

  const campo = (nombre, etiqueta, extra = {}) => (
    <TextField
      fullWidth
      size="small"
      label={etiqueta}
      value={formData[nombre]}
      onChange={cambiar(nombre)}
      error={!!errores[nombre]}
      helperText={errores[nombre]}
      disabled={guardando}
      sx={estiloCampo}
      {...extra}
    />
  );

  const campoMonto = (nombre, etiqueta, extra = {}) => campo(nombre, etiqueta, {
    inputProps: { inputMode: 'decimal' },
    InputProps: { startAdornment: <InputAdornment position="start" sx={{ color: 'rgba(255,255,255,0.5)' }}>Bs.</InputAdornment> },
    ...extra
  });

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { bgcolor: '#131316', color: 'white', backgroundImage: 'none' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#1B1B1F' }}>
        {purchase ? 'Editar compra' : 'Registrar compra'}
        <IconButton onClick={onClose} disabled={guardando} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {errorAlGuardar && <Alert severity="error" sx={{ mb: 2 }}>{errorAlGuardar}</Alert>}

        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
          Copiá los datos tal como figuran en la factura del proveedor. El libro
          refleja el documento, no lo corrige.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            {campo('fecha', 'Fecha del documento', { type: 'date', InputLabelProps: { shrink: true } })}
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small" sx={estiloCampo}>
              <InputLabel id="tipo-doc-compra">Tipo de documento</InputLabel>
              <Select
                labelId="tipo-doc-compra"
                label="Tipo de documento"
                value={formData.tipoDocumento}
                onChange={cambiar('tipoDocumento')}
                disabled={guardando}
              >
                {TIPOS_DOCUMENTO.map((t) => (
                  <MenuItem key={t.valor} value={t.valor}>{t.etiqueta}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            {esNota && campo('documentoAfectado', 'Factura que ajusta')}
          </Grid>

          <Grid item xs={12} sm={4}>{campo('rifProveedor', 'RIF del proveedor')}</Grid>
          <Grid item xs={12} sm={8}>{campo('nombreProveedor', 'Nombre del proveedor')}</Grid>

          <Grid item xs={12} sm={4}>{campo('numeroFactura', 'Número de documento')}</Grid>
          <Grid item xs={12} sm={4}>{campo('numeroControl', 'Número de control')}</Grid>

          <Grid item xs={12}><Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} /></Grid>

          {ALICUOTAS.map((a) => (
            <Grid item xs={12} sm={6} key={a.clave} container spacing={2}>
              <Grid item xs={6}>
                {campoMonto(a.base, `Base ${ALICUOTAS_IVA[a.clave].porcentaje}%`, { onBlur: sugerirIva(a) })}
              </Grid>
              <Grid item xs={6}>
                {campoMonto(a.iva, `IVA ${ALICUOTAS_IVA[a.clave].porcentaje}%`)}
              </Grid>
            </Grid>
          ))}

          <Grid item xs={12} sm={3}>{campoMonto('exento', 'Exento')}</Grid>
          <Grid item xs={12} sm={3}>{campoMonto('noGravado', 'No gravado')}</Grid>
          <Grid item xs={12} sm={3}>{campoMonto('ivaRetenido', 'IVA retenido')}</Grid>
          <Grid item xs={12} sm={3}>{campo('comprobanteRetencion', 'Nº comprobante')}</Grid>

          <Grid item xs={12}><Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} /></Grid>

          <Grid item xs={12} sm={5}>
            {campoMonto('totalConIva', 'Total del documento')}
          </Grid>
          <Grid item xs={12} sm={7}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                Suma de bases, exentos e IVA: <b>Bs. {bs(sumaDeComponentes)}</b>
              </Typography>
              {Math.abs(diferencia) > 0.10 && (
                <Typography variant="body2" sx={{ color: '#F5A623', mt: 0.5 }}>
                  Difiere del total en Bs. {bs(Math.abs(diferencia))}. Revisá los montos:
                  el servidor no acepta una diferencia mayor a diez céntimos.
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            {campo('notas', 'Notas', { multiline: true, rows: 2 })}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#1B1B1F', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <SecondaryButton onClick={onClose} disabled={guardando}>Cancelar</SecondaryButton>
        <ActionButton
          onClick={guardar}
          disabled={guardando}
          startIcon={guardando ? <CircularProgress size={16} sx={{ color: '#1C1525' }} /> : <SaveIcon />}
        >
          {guardando ? 'Guardando…' : 'Guardar'}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseForm;
